<template>
    <div class="flow-version-picker" :class="{ 'picker-compact': compact }">
        <!-- Flow Selector (hidden in versionOnly mode) -->
        <v-autocomplete
            v-if="!versionOnly"
            v-model="selectedFlowGroupId"
            :items="flowList"
            item-title="name"
            item-value="flowGroupID"
            :label="label"
            density="compact"
            variant="outlined"
            hide-details
            class="picker-select"
            :loading="loadingFlows"
            @update:modelValue="onFlowSelected"
            clearable
        >
            <template v-slot:item="{ item, props: itemProps }">
                <v-list-item v-bind="itemProps">
                    <template v-slot:prepend>
                        <v-icon size="small" color="primary">mdi-chart-timeline-variant-shimmer</v-icon>
                    </template>
                    <v-list-item-subtitle>
                        v{{ item.raw.version }} · {{ item.raw.createBy || 'Unknown' }}
                    </v-list-item-subtitle>
                </v-list-item>
            </template>
        </v-autocomplete>

        <!-- Version Selector (shown after flow is selected) -->
        <v-select
            v-if="versions.length > 0"
            v-model="selectedVersionId"
            :items="versions"
            :item-title="versionTitle"
            item-value="id"
            label="Version"
            density="compact"
            variant="outlined"
            hide-details
            class="picker-select"
            :class="{ 'mt-2': !compact, 'ml-2': compact && !versionOnly }"
            :loading="loadingVersions"
            @update:modelValue="onVersionSelected"
        >
            <template v-slot:item="{ item, props: itemProps }">
                <v-list-item v-bind="itemProps">
                    <template v-slot:prepend>
                        <v-chip
                            :color="item.raw.activeFlag ? 'success' : 'grey'"
                            size="x-small"
                            class="mr-2"
                        >
                            v{{ item.raw.version }}
                        </v-chip>
                    </template>
                    <v-list-item-subtitle class="text-caption">
                        {{ formatDate(item.raw.createDate) }} · {{ item.raw.createBy || 'Unknown' }}
                        <span v-if="item.raw.versionComment" class="ml-1 text-grey">
                            — {{ item.raw.versionComment }}
                        </span>
                    </v-list-item-subtitle>
                </v-list-item>
            </template>
        </v-select>

        <!-- Selected info chip (only when NOT compact) -->
        <v-chip
            v-if="selectedVersionId && !compact"
            size="small"
            color="primary"
            variant="tonal"
            class="mt-2"
            closable
            @click:close="clearSelection"
        >
            <v-icon start size="small">mdi-chart-timeline-variant-shimmer</v-icon>
            {{ selectedFlowName }} v{{ selectedVersion?.version || '?' }}
        </v-chip>
    </div>
</template>

<script>
export default {
    name: 'FlowVersionPicker',
    props: {
        label: { type: String, default: 'Select Flow' },
        /** Pre-select a specific flow version ID */
        preSelectedFlowId: { type: String, default: null },
        /** Pre-select a flow group ID */
        preSelectedGroupId: { type: String, default: null },
        /** Exclude this flow version ID from selection (to prevent comparing same version) */
        excludeFlowId: { type: String, default: null },
        /** Compact inline mode for toolbar use */
        compact: { type: Boolean, default: false },
        /** Version-only mode: hides flow selector, shows only versions for the pre-selected group */
        versionOnly: { type: Boolean, default: false },
    },
    emits: ['selected', 'cleared'],
    data() {
        return {
            flowList: [],
            versions: [],
            selectedFlowGroupId: null,
            selectedVersionId: null,
            selectedFlowName: '',
            loadingFlows: false,
            loadingVersions: false,
        };
    },
    computed: {
        selectedVersion() {
            return this.versions.find(v => v.id === this.selectedVersionId);
        }
    },
    watch: {
        preSelectedFlowId: {
            handler(newVal) {
                if (newVal && this.flowList.length > 0) {
                    this.preselectById(newVal);
                }
            },
            immediate: false
        },
        preSelectedGroupId: {
            handler(newVal) {
                if (newVal && this.versionOnly && !this.versions.length) {
                    // Version-only mode: load versions directly when group becomes available
                    this.selectedFlowGroupId = newVal;
                    this.loadVersionsForGroup(newVal);
                } else if (newVal && this.flowList.length > 0 && !this.selectedFlowGroupId) {
                    this.selectedFlowGroupId = newVal;
                    this.onFlowSelected(newVal);
                }
            },
            immediate: false
        }
    },
    mounted() {
        if (this.versionOnly && this.preSelectedGroupId) {
            // In version-only mode, skip flow list, load versions directly
            this.selectedFlowGroupId = this.preSelectedGroupId;
            this.loadVersionsForGroup(this.preSelectedGroupId);
        } else {
            this.loadFlowList();
        }
    },
    methods: {
        versionTitle(item) {
            const current = item.activeFlag ? ' (Current)' : '';
            return `v${item.version}${current} — ${item.versionComment || 'No comment'}`;
        },

        async loadFlowList() {
            this.loadingFlows = true;
            this.$call.GetFlowDropdown(
                (response) => {
                    if (response.status === 0) {
                        this.flowList = response.data || [];
                        // If preSelectedFlowId or preSelectedGroupId, auto-select
                        if (this.preSelectedFlowId) {
                            this.preselectById(this.preSelectedFlowId);
                        } else if (this.preSelectedGroupId) {
                            this.selectedFlowGroupId = this.preSelectedGroupId;
                            this.onFlowSelected(this.preSelectedGroupId);
                        }
                    }
                    this.loadingFlows = false;
                },
                () => {
                    this.loadingFlows = false;
                }
            );
        },

        async preselectById(flowId) {
            // We need to find which flow group this ID belongs to
            // Try fetching the flow to get its groupId
            this.$call.GetFlowById(flowId,
                (response) => {
                    if (response.status === 0 && response.data) {
                        const flow = response.data;
                        this.selectedFlowGroupId = flow.flowGroupID;
                        this.selectedFlowName = flow.name;
                        this.loadVersions(flow.flowGroupID, flowId);
                    }
                },
                () => {}
            );
        },

        onFlowSelected(groupId) {
            if (!groupId) {
                this.clearSelection();
                return;
            }
            const flow = this.flowList.find(f => f.flowGroupID === groupId);
            this.selectedFlowName = flow?.name || '';
            this.versions = [];
            this.selectedVersionId = null;
            this.loadVersions(groupId);
        },

        /** Load versions directly by groupId (used in versionOnly mode) */
        loadVersionsForGroup(groupId) {
            this.loadingVersions = true;
            // Also try to get the flow name from versions or flow list
            this.$call.GetFlowVersions(groupId,
                (response) => {
                    if (response.status === 0) {
                        let versions = response.data || [];
                        if (versions.length > 0 && !this.selectedFlowName) {
                            this.selectedFlowName = versions[0].name || '';
                        }
                        if (this.excludeFlowId) {
                            versions = versions.filter(v => v.id !== this.excludeFlowId);
                        }
                        this.versions = versions.sort((a, b) => b.version - a.version);
                        // If preSelectedFlowId provided, auto-select that version
                        if (this.preSelectedFlowId) {
                            const found = this.versions.find(v => v.id === this.preSelectedFlowId);
                            if (found) {
                                this.selectedVersionId = found.id;
                                this.onVersionSelected(found.id);
                            }
                        }
                    }
                    this.loadingVersions = false;
                },
                () => {
                    this.loadingVersions = false;
                }
            );
        },

        loadVersions(groupId, preSelectVersionId = null) {
            this.loadingVersions = true;
            this.$call.GetFlowVersions(groupId,
                (response) => {
                    if (response.status === 0) {
                        let versions = response.data || [];
                        // Filter out excluded version
                        if (this.excludeFlowId) {
                            versions = versions.filter(v => v.id !== this.excludeFlowId);
                        }
                        this.versions = versions.sort((a, b) => b.version - a.version);
                        
                        // Auto-select if preSelectVersionId provided
                        if (preSelectVersionId) {
                            const found = this.versions.find(v => v.id === preSelectVersionId);
                            if (found) {
                                this.selectedVersionId = found.id;
                                this.onVersionSelected(found.id);
                            }
                        }
                        // If only one version, auto-select it
                        else if (this.versions.length === 1) {
                            this.selectedVersionId = this.versions[0].id;
                            this.onVersionSelected(this.versions[0].id);
                        }
                    }
                    this.loadingVersions = false;
                },
                () => {
                    this.loadingVersions = false;
                }
            );
        },

        onVersionSelected(versionId) {
            if (!versionId) return;
            const version = this.versions.find(v => v.id === versionId);
            this.$emit('selected', {
                flowId: versionId,
                flowGroupId: this.selectedFlowGroupId,
                flowName: this.selectedFlowName,
                version: version?.version,
                versionComment: version?.versionComment,
                createBy: version?.createBy,
                createDate: version?.createDate,
                activeFlag: version?.activeFlag,
            });
        },

        clearSelection() {
            this.selectedFlowGroupId = null;
            this.selectedVersionId = null;
            this.selectedFlowName = '';
            this.versions = [];
            this.$emit('cleared');
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            try {
                const date = new Date(dateString);
                return date.toLocaleDateString('en-GB', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                });
            } catch {
                return dateString;
            }
        }
    }
};
</script>

<style scoped>
.flow-version-picker {
    min-width: 250px;
}

/* Compact mode: flow + version side by side */
.picker-compact {
    display: flex;
    flex-direction: row;
    align-items: center;
    min-width: unset;
    gap: 0;
}

.picker-compact .picker-select {
    flex: 1;
    min-width: 120px;
    max-width: 240px;
}

.picker-select {
    max-width: 350px;
}

.picker-select :deep(.v-field) {
    border-radius: 10px;
    font-size: 0.875rem;
}
</style>
